import { type TooltipPositionerCallbackFunction } from '../types';

type SeriesBarMetrics = {
  barW?: number;
  columnIndex?: number;
  columnMetrics?: {
    columnCount: number;
    offset: number;
    paddedWidth: number;
    width: number;
  };
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(min, value), Math.max(min, max));

/**
 * Place the tooltip below the hovered bar group, flipping above when it would overflow the plot.
 *
 * Positioner `plotY` is one bar's visual center (from Highcharts `getAnchor` → `tooltipPos`).
 * `columnMetrics.paddedWidth * columnCount` is the full dodge-group height; `columnIndex` places
 * this series' bar inside that group so we clear every sibling bar.
 */
export const tooltipPositionBelow: TooltipPositionerCallbackFunction = (
  tooltip,
  labelWidth,
  labelHeight,
  point,
) => {
  const { chart } = tooltip;
  const { plotLeft, plotTop, plotWidth, plotHeight, inverted } = chart;
  const plotRight = plotLeft + plotWidth;
  const gap = 8;

  const anchorX = point.plotX ?? 0;
  const anchorY = point.plotY ?? 0;
  const series = point.series as SeriesBarMetrics | undefined;
  const metrics = series?.columnMetrics;
  const thickness = series?.barW ?? metrics?.width ?? 0;
  const paddedWidth = metrics?.paddedWidth ?? thickness;
  const columnCount = Math.max(metrics?.columnCount ?? 1, 1);
  const columnIndex = series?.columnIndex ?? 0;

  // paddedWidth is the stride between bar centers; group spans columnCount slots.
  const barTop = anchorY - thickness / 2 - columnIndex * paddedWidth;
  const barBottom =
    anchorY + thickness / 2 + (columnCount - 1 - columnIndex) * paddedWidth;

  const x = clamp(
    inverted ? plotLeft + anchorX / 2 - labelWidth / 2 : plotLeft + anchorX - labelWidth / 2,
    plotLeft + 5,
    plotRight - labelWidth - 5,
  );

  const belowY = plotTop + barBottom + gap;
  const aboveY = plotTop + barTop - labelHeight - gap;
  const y = barBottom + gap + labelHeight > plotHeight ? aboveY : belowY;

  return { x, y };
};
